import React from "react";
import { Alert } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { buildEchartsOptions } from "../../utils/chartTheme";
import {
  alignChartSeries,
  seasonChartHasValues,
} from "../../utils/seasonChartSeries";

export function buildCareerArcOption({
  namedSeries = [],
  xAxisName = "Season",
  yAxisName,
  mode = "season",
}) {
  const aligned = alignChartSeries(namedSeries, { mode });
  const multi = namedSeries.length > 1;

  return buildEchartsOptions({
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const lines = (params || []).map((item) => {
          const display = item.data == null ? "—" : item.data;
          return `${item.marker}${item.seriesName}: ${display}`;
        });
        const axis = params?.[0]?.axisValue ?? "";
        return [`${xAxisName}: ${axis}`, ...lines].join("<br/>");
      },
    },
    legend: multi
      ? { data: namedSeries.map((item) => item.name), top: 0 }
      : undefined,
    grid: {
      left: "5%",
      right: "4%",
      bottom: "12%",
      containLabel: true,
      top: multi ? 60 : 24,
    },
    xAxis: {
      type: "category",
      data: aligned.years,
      name: xAxisName,
      nameLocation: "middle",
      nameGap: 45,
      axisLabel: {
        show: true,
        margin: 15,
      },
    },
    yAxis: {
      type: "value",
      name: yAxisName,
      nameLocation: "middle",
      nameGap: 60,
      axisLabel: {
        show: true,
        margin: 15,
      },
    },
    series: aligned.series.map((item) => ({
      name: item.name,
      type: "line",
      data: item.data,
      connectNulls: true,
      symbol: "circle",
      symbolSize: 8,
      emphasis: {
        focus: "series",
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      },
    })),
  });
}

const CareerArcChart = ({
  namedSeries = [],
  xAxisName = "Season",
  yAxisName,
  mode = "season",
  emptyMessage = "No season values for this stat.",
  height = 420,
}) => {
  const hasValues = namedSeries.some((item) => seasonChartHasValues(item.points));
  if (!hasValues) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <ReactECharts
      option={buildCareerArcOption({
        namedSeries,
        xAxisName,
        yAxisName,
        mode,
      })}
      style={{ height }}
      notMerge={true}
    />
  );
};

export default CareerArcChart;
